import type { RentalContract } from "shared/types/rental-contracts";

export type IndexationActiveTab = "never" | "pending" | "indexed";

const VALID_TABS = new Set<IndexationActiveTab>(["never", "pending", "indexed"]);

/** Guard: returns the cached value if still valid, otherwise falls back to 'pending'. */
export function safeActiveTab(value: string | undefined): IndexationActiveTab {
  if (value && VALID_TABS.has(value as IndexationActiveTab)) {
    return value as IndexationActiveTab;
  }
  return "pending";
}

interface IndexationCacheData {
  contracts: RentalContract[];
  currentPage: number;
  total: number;
  activeTab: IndexationActiveTab;
  filters: {
    searchText?: string;
    buildingIds?: string[];
    timeRangeFilter?: string[];
    indexationStatusFilter?: string[];
  };
  groupBy: string;
}

let cache: IndexationCacheData | null = null;

export function getCache(): IndexationCacheData | null {
  return cache;
}

export function setCache(data: Partial<IndexationCacheData>): void {
  if (cache) {
    cache = { ...cache, ...data };
  } else {
    cache = {
      contracts: data.contracts ?? [],
      currentPage: data.currentPage ?? 1,
      total: data.total ?? 0,
      activeTab: data.activeTab ?? "pending",
      filters: data.filters ?? {},
      groupBy: data.groupBy ?? "none",
    };
  }
}

export function clearCache(): void {
  cache = null;
}

/**
 * Update a single contract in the cache after a mutation (e.g. indexation applied).
 */
export function updateContractInCache(
  contractId: number,
  updates: Partial<import("shared/types/rental-contracts").RentalContract>,
): void {
  if (!cache) return;

  cache.contracts = cache.contracts.map((c) =>
    c.id === contractId ? { ...c, ...updates } : c,
  );
}

/**
 * Remove a contract from the cache after it has been processed (e.g. an
 * indexation was applied), so it doesn't reappear when the list is restored.
 */
export function removeContractFromCache(contractId: number): void {
  if (!cache) return;

  cache.contracts = cache.contracts.filter((c) => c.id !== contractId);
  if (typeof cache.total === "number" && cache.total > 0) {
    cache.total -= 1;
  }
}
